import { FC, useEffect, useState } from "react";
import { Button, Stack, Text } from "@chakra-ui/react";
import { useFlag, useVariant } from "@unleash/proxy-client-react";

import { UnleashFlags } from "../../../../../../constants";
import { PaymentModalStatus } from "./PaymentModalStatus.tsx";
import { PaymentModalDetails } from "./PaymentModalDetails.tsx";
import { formatPriceWithoutZeros, isStringWithNumber } from "../../../../../../utils";
import { PaymentModalPaymentMethod } from "./PaymentModalPaymentMethod.tsx";
import { queryClient } from "../../../../../../api";
import { ICard } from "../../../../../../api/cards/types.ts";
import { useMakePaymentMutation } from "../../../../../../api/payments";
import { PaymentDestinationType, PaymentPollingStatusType } from "../../../../../../api/payments/types.ts";

const STATUSES_WITHOUT_PAYMENT_REPETITION: PaymentPollingStatusType[] = [
  "DONE",
  "IN_PROGRESS",
  "COUNT_LIMIT_EXCEEDED_SBP_FIN",
  "ACCOUNT_BINDING_NOT_FOUND_SBP_FIN",
];

interface Props {
  amount: string;
  paymentMethod: ICard;
  paymentDestination: PaymentDestinationType;
  resetPaymentDestination: () => void;
  clearInput: () => void;
  onCancel: () => void;
  hideModal: () => void;
  blockModalClosing: (state: boolean) => void;
}

export const PaymentBlockPaymentModal: FC<Props> = (props) => {
  const {
    amount,
    paymentMethod,
    paymentDestination,
    resetPaymentDestination,
    clearInput,
    onCancel,
    hideModal,
    blockModalClosing,
  } = props;

  const { mutateAsync, isPending } = useMakePaymentMutation();

  const [isPaymentAvailable, setIsPaymentAvailable] = useState<boolean>(true);
  const [status, setStatus] = useState<PaymentPollingStatusType | undefined>();

  const paymentAmount = Number(amount);
  const isDebtRepaymentOptionsEnabled = useFlag(UnleashFlags.debtRepaymentOptionsEnabled);

  const commissionVariant = useVariant(UnleashFlags.dfPaymentsCommission);
  const commissionValue = commissionVariant.payload?.value;
  const commissionAmount = paymentMethod.terminalType === "PAID" && isStringWithNumber(commissionValue)
    ? paymentAmount * (Number(commissionValue) / 100)
    : 0;

  const totalAmount = formatPriceWithoutZeros(paymentAmount + commissionAmount);

  const onPay = async () => {
    blockModalClosing(true);
    setStatus(undefined);

    try {
      const response = await mutateAsync({
        productId: paymentMethod.productId || "",
        amount,
        ...(isDebtRepaymentOptionsEnabled && paymentDestination && { paymentDestination: paymentDestination }),
      });
      setStatus(response.statusCode);

      if (STATUSES_WITHOUT_PAYMENT_REPETITION.includes(response.statusCode!)) {
        setIsPaymentAvailable(false);
        resetPaymentDestination();
      }

      if (response.statusCode === "DONE" || response.statusCode === "IN_PROGRESS") {
        clearInput();
        void queryClient.invalidateQueries();
      }
    } catch (err) {
      setStatus("ERROR");
      console.error(err);
    } finally {
      blockModalClosing(false);
    }
  };

  useEffect(() => {
    if (paymentMethod.terminalType === "PAID" && !isStringWithNumber(commissionValue)) {
      setTimeout(() => {
        onCancel();
      });
    }
  }, []);

  return (
    <Stack pt="55px" px="4px" spacing={0}>
      <Text variant="brand" fontSize="40px" textAlign="center" mb="17px">{totalAmount}</Text>
      <Text variant="brand" textAlign="center" mb="24px">Сумма платежа</Text>

      <PaymentModalDetails paymentAmount={paymentAmount} commissionAmount={commissionAmount} paymentMethod={paymentMethod} />
      <PaymentModalPaymentMethod isPending={isPending} hideModal={hideModal} paymentMethod={paymentMethod} />
      <PaymentModalStatus status={status} />

      <Stack mt="29px">
        {isPaymentAvailable
          ? <Button colorScheme="brand" onClick={onPay} isLoading={isPending}>Оплатить</Button>
          : <Button colorScheme="gray" onClick={onCancel}>Закрыть</Button>}
      </Stack>
    </Stack>
  );
};
